import { forwardRef, useState } from "react";
import { Typography } from "@mui/material";
import {
  ANALYZING_CREATIVE_TEMPLATE_ERROR,
  ANALYZING_CREATIVE_TEMPLATE_MESSAGE,
  ANALYZING_CREATIVE_TEMPLATE_SUCCESS,
  DISCARD_ANALYZING_CREATIVE_TEMPLATE_ALERT_DESCRIPTION,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import { CREATIVE_JOB_TYPES } from "../../utils/errors";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertErrorMessage from "./TaskAlertErrorMessage";
import TaskAlertDiscardActions from "./TaskAlertDiscardActions";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";
import { useTemplatesModals } from "../../contexts/templatesModalsContext";

const getTaskVisualStatus = ({ isError, isSuccess, isLoading }) => {
  if (isError) return "error";
  if (isSuccess) return "success";
  if (isLoading) return "inProgress";
  return "pending";
};

const AnalyzingCreativeTemplateAlertItem = forwardRef(
  ({ alert, onClose }, ref) => {
    const { t } = useTranslationApp();
    const { openTemplateEditor } = useTemplatesModals();
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
    const { id, status, progress, generationData, message, messageKey, jobID } =
      alert;

    const isError = status === ALERTS_ASYNC_STATUS.ERROR;
    const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
    const isLoading =
      status === ALERTS_ASYNC_STATUS.LOADING ||
      status === ALERTS_ASYNC_STATUS.GENERATING;
    const isTerminal = isError || isSuccess;
    const storageKey = jobID ?? id;

    const {
      loaderTimeMs,
      onProgressChange,
      seedProgress,
      formattedRemainingTime,
    } = useCreativeJobProgressLoader({
      storageKey,
      migrateFromKey: id,
      jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
      isTerminal,
    });

    const getTranslatedMessage = () => {
      if (isError) return t(ANALYZING_CREATIVE_TEMPLATE_ERROR);
      if (isSuccess) return t(ANALYZING_CREATIVE_TEMPLATE_SUCCESS);
      if (messageKey) return t(messageKey);
      return t(ANALYZING_CREATIVE_TEMPLATE_MESSAGE);
    };

    const onAlertClick = () => {
      if (isConfirmingDiscard) return;
      const templateID = generationData?.templateID;
      if (!templateID) return;
      openTemplateEditor({ templateID });
    };

    const taskVisualStatus = getTaskVisualStatus({
      isError,
      isSuccess,
      isLoading,
    });
    const displayProgress = progress || (isLoading ? seedProgress : 100);
    const showDiscardButton = isError || isSuccess;

    return (
      <TaskAlertCard
        ref={ref}
        status={taskVisualStatus}
        label={getTranslatedMessage()}
        estimatedTime={isLoading ? formattedRemainingTime : null}
        progress={displayProgress}
        onClick={onAlertClick}
        cursor="pointer"
        showProgressBar={!isLoading}
        showProgressPercent={!isLoading}
        progressSlot={
          isLoading ? (
            <LinearLoaderWithTime
              isAlert={true}
              labelSide="bottom"
              time={loaderTimeMs}
              progress={seedProgress}
              autoIncrement={true}
              max={99}
              onProgressChange={onProgressChange}
            />
          ) : null
        }
        details={
          message ? (
            isError ? (
              <TaskAlertErrorMessage>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  {message}
                </Typography>
              </TaskAlertErrorMessage>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {message}
              </Typography>
            )
          ) : null
        }
        actions={
          showDiscardButton ? (
            <TaskAlertDiscardActions
              isConfirming={isConfirmingDiscard}
              descriptionKey={
                DISCARD_ANALYZING_CREATIVE_TEMPLATE_ALERT_DESCRIPTION
              }
              onStartDiscard={() => {
                if (isSuccess) {
                  setIsConfirmingDiscard(true);
                  return;
                }
                onClose(id);
              }}
              onConfirm={() => onClose(id)}
              onCancel={() => setIsConfirmingDiscard(false)}
              onView={isSuccess ? onAlertClick : null}
            />
          ) : null
        }
      />
    );
  }
);

AnalyzingCreativeTemplateAlertItem.displayName =
  "AnalyzingCreativeTemplateAlertItem";

export default AnalyzingCreativeTemplateAlertItem;
